import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { decodeArrayBufferErrorDetail } from "../../../utils/arrayBufferErrorDetail";
import type { PbjSubmissionProcessType } from "../../../utils/models";
import { downloadRawExport, downloadSubmissionExport } from "../pbjDownloads";
import { usePbjNetwork } from "./PbjNetworkContext";

export type NetworkDownloadKind = "raw" | "submission";

const MISSING_HEADER_KEY = "pbj_model.audit.submission_export.missing_header";

/** Why one building's file failed, in a few words that fit a list: the server's own reason when it sent one. */
const failureReasonKey = (error: unknown): string => {
	const detail = decodeArrayBufferErrorDetail(error);
	if (detail?.message === MISSING_HEADER_KEY) return "pbj_model.network.download.reason.missing_facility";
	if (detail?.error_type === "UIVisibleError" && detail.message) return detail.message;
	return "pbj_model.network.download.reason.failed";
};
export type NetworkDownloadProgress = { done: number; total: number };

/**
 * "Download all selected buildings": the same per-building files as a single download, one after
 * another, for every building the Building filter selects (every building when none is checked).
 * A building that fails doesn't stop the rest; the failures are named once at the end.
 */
export const useNetworkDownloads = (startDate: string, endDate: string, processType: PbjSubmissionProcessType) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const { buildings, selectedBuildingIds, homeDepartmentId } = usePbjNetwork();
	const [progress, setProgress] = useState<NetworkDownloadProgress | null>(null);
	const targets = buildings.filter((building) => selectedBuildingIds.size === 0 || selectedBuildingIds.has(building.departmentId));

	const downloadAll = useCallback(
		async (kind: NetworkDownloadKind) => {
			const properties = { department_id: homeDepartmentId, kind, building_count: targets.length };
			track("pbj_network_download_start", properties);
			const failures: string[] = [];
			setProgress({ done: 0, total: targets.length });
			for (const [index, building] of targets.entries()) {
				const request = { departmentId: building.departmentId, buildingName: building.name, startDate, endDate };
				try {
					// One at a time on purpose: each is a full export, and the browser saves each file as it lands.
					await (kind === "raw" ? downloadRawExport(request) : downloadSubmissionExport(request, processType));
				} catch (error) {
					failures.push(t("pbj_model.network.download.failure", { building: building.name, reason: t(failureReasonKey(error)) }));
					trackError("pbj_network_download_error", { ...properties, failed_department_id: building.departmentId, error_message: String(error) });
				}
				setProgress({ done: index + 1, total: targets.length });
			}
			setProgress(null);
			if (failures.length > 0) {
				showSnackbar(t("pbj_model.network.download.failed", { count: failures.length, failures: failures.join("; ") }), "error");
				return;
			}
			track("pbj_network_download_success", properties);
			showSnackbar(t("pbj_model.network.download.done", { count: targets.length }), "success");
		},
		[targets, homeDepartmentId, startDate, endDate, processType, track, trackError, showSnackbar, t],
	);

	return { downloadAll, progress, targetCount: targets.length };
};
